/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { Tooltip, Checkbox, Typography, Grid, CardContent } from "@material-ui/core";
import SurveyQuestionCard from "./SurveyQuestionCard";
import FormTextField from "../../../components/FormTextField/FormTextField";
import { WEB_CHAT_TOOL_TIPS } from "../../../utility/toolTipMessages";
import { SURVEY_QUESTION_TYPE } from "../../../utility/constants";
import { testForRangeOverlap } from "../../../utility";
import { ENDPOINTS } from "../../../utility/Endpoints";
import { ActionButtons } from "../../../components/Common";

const newSurveyQuestion = () => ({
	question: "",
	followUpQuestions: [],
	questionType: SURVEY_QUESTION_TYPE.SCALE,
});

/**
 * This component is used on the Chat configuration page to render the various neccesary fields for editing the web chat widgets configuration.
 * @param {Object} selectedConfiguration The currently selected chat configuration to edit.
 * @param {(Object) => void} setSelectedConfiguration Callback method to set the currently selected chat configuration to edit.
 */
const SurveyForm = ({
	selectedConfiguration,
	setSelectedConfiguration,
	setFormEdited,
	formEdited,
	setValidationMessage,
}) => {
	const setConfigValue = (valueName, value) => {
		setFormEdited(true);
		let updateConfig = {
			...selectedConfiguration,
			submitSurveyAnswerURL: `${ENDPOINTS.ADMIN_API}/submitSurveyAnswer`, //Backwards compatibilty. Should be removed eventually.
		};
		updateConfig[valueName] = value;
		setSelectedConfiguration(updateConfig);

		let newValidationMessage = undefined;
		if (updateConfig.surveysEnabled) {
			if (updateConfig.surveyQuestions && updateConfig.surveyQuestions.length > 0) {
				updateConfig.surveyQuestions.forEach((question) => {
					if (!newValidationMessage) {
						if (question.question.trim().length === 0) {
							newValidationMessage = "One or more survey questions are blank.";
						}
						if (!newValidationMessage && question.followUpQuestions && question.followUpQuestions.length > 0) {
							question.followUpQuestions.forEach((followUp, index) => {
								newValidationMessage =
									!followUp.question || followUp.question.trim().length === 0
										? "One or more follow up questions are blank."
										: question.followUpQuestions.find(
												(element, elementIndex) =>
													index !== elementIndex &&
													(question.questionType === SURVEY_QUESTION_TYPE.SCALE
														? testForRangeOverlap(element.range, followUp.range)
														: element.binaryAnswer === followUp.binaryAnswer),
										  ) !== undefined
										? "One or more set of follow up questions have overlapping answer ranges."
										: undefined;
							});
						}
					}
				});
			} else {
				//surveys enabled but no questions
				newValidationMessage = "One or more survey questions are required while surveys are enabled.";
			}
		}
		setValidationMessage(newValidationMessage);
	};

	return (
		<CardContent>
			<Grid container spacing={3}>
				<Grid item xs={6} className="mt-3 pb-0">
					<Typography style={{ fontSize: 12, color: "#0000008A", lineHeight: 1 }}>Enable Surveys</Typography>
					<Tooltip arrow interactive placement={"bottom-start"} title={WEB_CHAT_TOOL_TIPS.ENABLE_SURVEYS_CHECKBOX}>
						<i style={{ height: "15px", margin: "12px 12px 12px 0", color: "gray" }} className="fas fa-info-circle" />
					</Tooltip>
					<Checkbox
						checked={selectedConfiguration.surveysEnabled || false}
						onChange={(event) => {
							setConfigValue("surveysEnabled", event.target.checked);
						}}
					/>
				</Grid>
				<Grid item xs={6} className="mt-3 pb-0">
					<Typography
						style={{
							fontSize: 12,
							color: !selectedConfiguration.surveysEnabled ? "#00000061" : "#0000008A",
							lineHeight: 1,
						}}
					>
						Always Offer Surveys
					</Typography>
					<Tooltip
						arrow
						interactive
						placement={"bottom-start"}
						title={WEB_CHAT_TOOL_TIPS.ALWAYS_OFFER_SURVEYS_CHECKBOX}
					>
						<i style={{ height: "15px", margin: "12px 12px 12px 0", color: "gray" }} className="fas fa-info-circle" />
					</Tooltip>
					<Checkbox
						disabled={!selectedConfiguration.surveysEnabled}
						checked={selectedConfiguration.alwaysOfferSurvey || false}
						onChange={(event) => {
							setConfigValue("alwaysOfferSurvey", event.target.checked);
						}}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<FormTextField
						label={"Survey Selected Rating Image"}
						formEdited={formEdited}
						setEdited={setFormEdited}
						styleObject={{ margin: 0, width: "100%" }}
						disabled={!selectedConfiguration.surveysEnabled}
						toolTipMessage={WEB_CHAT_TOOL_TIPS.SURVEY_SELECTED_RATING_IMAGE}
						startingValue={selectedConfiguration.surveySelectedRatingImage || ""}
						setConfigValue={setConfigValue}
						valueName={"surveySelectedRatingImage"}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<FormTextField
						label={"Survey Unselected Rating Image"}
						formEdited={formEdited}
						setEdited={setFormEdited}
						styleObject={{ margin: 0, width: "100%" }}
						disabled={!selectedConfiguration.surveysEnabled}
						toolTipMessage={WEB_CHAT_TOOL_TIPS.SURVEY_UNSELECTED_RATING_IMAGE}
						startingValue={selectedConfiguration.surveyUnselectedRatingImage || ""}
						setConfigValue={setConfigValue}
						valueName={"surveyUnselectedRatingImage"}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<FormTextField
						label={"Survey Introduction Message"}
						formEdited={formEdited}
						setEdited={setFormEdited}
						styleObject={{ margin: 0, width: "100%" }}
						disabled={!selectedConfiguration.surveysEnabled}
						toolTipMessage={WEB_CHAT_TOOL_TIPS.SURVEY_INTRODUCTION_MESSAGE}
						startingValue={selectedConfiguration.surveyIntroductionMessage || ""}
						setConfigValue={setConfigValue}
						valueName={"surveyIntroductionMessage"}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<FormTextField
						label={"Survey Complete Message"}
						formEdited={formEdited}
						setEdited={setFormEdited}
						styleObject={{ margin: 0, width: "100%" }}
						disabled={!selectedConfiguration.surveysEnabled}
						toolTipMessage={WEB_CHAT_TOOL_TIPS.SURVEY_COMPLETE_MESSAGE}
						startingValue={selectedConfiguration.surveyCompleteMessage || ""}
						setConfigValue={setConfigValue}
						valueName={"surveyCompleteMessage"}
					/>
				</Grid>
			</Grid>
			<div style={{ marginTop: "10px" }}>
				{selectedConfiguration.surveyQuestions.map((surveyQuestion, index) => {
					return (
						<SurveyQuestionCard
							key={`surveyQuestion${index}`}
							formEdited={formEdited}
							setFormEdited={setFormEdited}
							surveyQuestion={{ ...surveyQuestion, index }}
							surveyQuestionsLength={selectedConfiguration.surveyQuestions.length}
							setSurveyQuestion={(newSurveyQuestionValue) => {
								const newSurveyQuestions = [...selectedConfiguration.surveyQuestions];
								newSurveyQuestions[index] = newSurveyQuestionValue;
								setConfigValue("surveyQuestions", newSurveyQuestions);
							}}
							removeSurveyQuestion={() => {
								const newSurveyQuestions = [...selectedConfiguration.surveyQuestions];
								newSurveyQuestions.splice(index, 1);
								setConfigValue("surveyQuestions", newSurveyQuestions);
							}}
							moveQuestionUp={() => {
								const newSurveyQuestions = [...selectedConfiguration.surveyQuestions];
								const temp = selectedConfiguration.surveyQuestions[index - 1];
								newSurveyQuestions[index - 1] = {
									...surveyQuestion,
								};
								newSurveyQuestions[index] = { ...temp };
								setConfigValue("surveyQuestions", newSurveyQuestions);
							}}
							moveQuestionDown={() => {
								const newSurveyQuestions = [...selectedConfiguration.surveyQuestions];
								const temp = selectedConfiguration.surveyQuestions[index + 1];
								newSurveyQuestions[index + 1] = {
									...surveyQuestion,
									index: index + 1,
								};
								newSurveyQuestions[index] = { ...temp };
								setConfigValue("surveyQuestions", newSurveyQuestions);
							}}
						/>
					);
				})}
			</div>
			<ActionButtons
				showCancel
				onCancel={() => {
					const newSurveyQuestions = [...selectedConfiguration.surveyQuestions];
					newSurveyQuestions.push(newSurveyQuestion());
					setConfigValue("surveyQuestions", newSurveyQuestions);
				}}
				cancelLabel="Add Survey Question"
			/>
		</CardContent>
	);
};

export default SurveyForm;
